import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { maskPhone } from "../../../utils/masks";
import { useConcessionariaBusca } from "../../../hooks/queries/cliente/useConcessionariaBusca/useConcessionariaBusca";
import { useDeliveryCidadesLista } from "../../../hooks/queries/delivery/useDeliveryCidadesLista/useDeliveryCidadesLista";
import { Tracking } from "../../../services/Tracking";
import { IAtendimentoConcessionariaForm } from "../../../types/agendamento";
import { ISelectOptions } from "../../../types/inputs";

export type IFormDeliveryConcessionaireV2Values = IAtendimentoConcessionariaForm & {
  cidade: string;
};

const schema = z.object({
  nome: z.string().min(1, "Campo obrigatorio"),
  telefone: z.string().min(9, "Campo obrigatorio"),
  cidade: z.string().min(1, "Você precisa selecionar uma cidade"),
  uuidConcessionaria: z.string().min(1, "Você precisa selecionar uma concessionária"),
});

export const useFormDeliveryConcessionaireV2 = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm<IFormDeliveryConcessionaireV2Values>({
    defaultValues: {
      nome: "",
      telefone: "",
      cidade: "",
      uuidConcessionaria: "",
    },
    resolver: zodResolver(schema),
    mode: "all",
  });

  const telefoneDigitado = watch("telefone");
  const cidadeSelecionada = watch("cidade");

  const { buscarConcessionarias } = useConcessionariaBusca();
  const { buscarCidades } = useDeliveryCidadesLista();
  const [selectedConcessionaria, setSelectedConcessionaria] =
    useState<ISelectOptions | null>(null);
  const [selectedCidade, setSelectedCidade] =
    useState<ISelectOptions | null>(null);

  const loadOptionsCidades = () =>
    buscarCidades()
      .then((cidades) =>
        cidades.map((cidade) => ({ value: cidade, label: cidade })),
      )
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          Tracking.error(
            "mapa:agendamento/domicilio:buscar-cidades-delivery-falhou",
            { mensagem },
          );
          toast.error(mensagem);
          return [];
        },
      );

  const loadOptions = (nome: string) =>
    buscarConcessionarias(nome, cidadeSelecionada)
      .then((data) =>
        data.content.map((item) => ({
          value: item.uuid,
          label: item.nome,
          element: item,
        })),
      )
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          Tracking.error(
            "mapa:agendamento/domicilio:buscar-concessionarias-falhou",
            { mensagem },
          );
          toast.error(mensagem);
          return [];
        },
      );

  const handleCidadeChange = (option: ISelectOptions) => {
    setSelectedCidade(option);
    setValue("cidade", option?.value ?? "");
    setSelectedConcessionaria(null);
    setValue("uuidConcessionaria", "");
  };

  useEffect(() => {
    setValue("telefone", maskPhone(telefoneDigitado));
  }, [telefoneDigitado, setValue]);

  return {
    control,
    handleSubmit,
    register,
    errors,
    loadOptions,
    loadOptionsCidades,
    selectedConcessionaria,
    setSelectedConcessionaria,
    selectedCidade,
    handleCidadeChange,
    cidadeSelecionada,
  };
};
